import { useObservableWindow } from "@faire/web/ui/hooks/useObservableWindow";
import { useState } from "@faire/web/ui/hooks/useState";
import React, { useLayoutEffect } from "react";

import { getSpacingValue } from "../../spacing/getSpacing";
import { Position } from "../common";
import { TOOLTIP_POINTER_SIZE } from "../consts";

/**
 * The minimum margin from the border of the viewport to the tooltip in case it needs to be moved
 */
const MIN_MARGIN_FROM_BORDER = getSpacingValue("1x");

/**
 * The minimum space between the tooltip tip and the end of the tooltip box for esthetic reasons
 */
const MIN_SPACE_BETWEEN_POINTER_AND_BOX_END = getSpacingValue("1/2x");

/**
 * The length of the tooltip pointer on the edge of the tooltip box
 *
 * It is the length of the hypotenuse of the triangle formed by the tooltip pointer,
 * which is a rotated square of size @TOOLTIP_POINTER_SIZE
 */
const TOOLTIP_POINTER_LENGTH = Math.sqrt(2) * TOOLTIP_POINTER_SIZE;

/**
 * Moves the tooltip box to the left or to the right if the current position is outside the viewport
 * Only acts for @position "top" and "bottom" and if @keepInsideViewport is true
 */
export const useTooltipOffsetToKeepTooltipInsideViewport = (
  keepInsideViewport: boolean,
  position: Position,
  pointerOffsetProvidedByClient: number,
  isHovered: boolean
) => {
  const tooltipBoxRef = React.useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const { width: windowWidth } = useObservableWindow();

  useLayoutEffect(() => {
    const positionTopOrBottom = position === "top" || position === "bottom";
    const tooltipBox = tooltipBoxRef.current;
    if (!positionTopOrBottom || !tooltipBox || !keepInsideViewport) {
      return;
    }

    const boundingBox = tooltipBox.getBoundingClientRect();
    const lengthFromLeftBorder = boundingBox.left;
    const lengthFromRightBorder = boundingBox.right;

    const tooltipOutOfViewportOnTheLeft = lengthFromLeftBorder < 0;
    if (tooltipOutOfViewportOnTheLeft) {
      const offsetToTheRight = lengthFromLeftBorder - MIN_MARGIN_FROM_BORDER;
      setOffset(offsetToTheRight);
    }

    const tooltipOutOfViewportOnTheRight = lengthFromRightBorder > windowWidth;
    if (tooltipOutOfViewportOnTheRight) {
      setOffset((currentOffset) => {
        return calculateNewOffsetToTheLeft(
          currentOffset,
          windowWidth,
          boundingBox
        );
      });
    }
  }, [keepInsideViewport, position, tooltipBoxRef, windowWidth, isHovered]);

  return {
    offset: keepInsideViewport ? offset : pointerOffsetProvidedByClient,
    tooltipBoxRef,
  };
};

const calculateNewOffsetToTheLeft = (
  currentOffset: number,
  windowWidth: number,
  boundingBox: DOMRect
) => {
  const offsetToTheLeft: number =
    boundingBox.right - windowWidth + MIN_MARGIN_FROM_BORDER;
  const offsetRelativeToExistingOffset = offsetToTheLeft + currentOffset;

  const maxOffsetBeforePointerDisconnectsBox: number =
    (boundingBox.width - TOOLTIP_POINTER_LENGTH) / 2.0 -
    MIN_SPACE_BETWEEN_POINTER_AND_BOX_END;

  const edgeOfTooltipReached: boolean =
    offsetRelativeToExistingOffset > maxOffsetBeforePointerDisconnectsBox;

  const newOffset: number = edgeOfTooltipReached
    ? maxOffsetBeforePointerDisconnectsBox
    : offsetRelativeToExistingOffset;

  return newOffset;
};
